/**
 * Community API — Supabase-backed posts, replies, likes.
 * Replaces the old static POSTS_DATA / MEMBERS mock data.
 */
import { avatarSrc } from "./avatar";
import { supabase } from "../../supabaseClient";

export function formatRelativeTime(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
}

// Prefer real name; fall back to handle (so two un-onboarded coaches don't
// both render as "Coach"); finally fall back to "Coach <XXXX>" derived from
// the profile id so every un-onboarded coach still reads as a distinct
// human. Better than a shared "Anonymous Coach" that made the whole
// community feed look broken when many coaches skipped profile setup.
// Historic callers pass an id-less object (or nothing) — in that case
// we still degrade to "Coach" rather than throwing.
export function displayName({ id, first_name, last_name, handle } = {}) {
  const full = [first_name, last_name].filter(Boolean).join(" ").trim();
  if (full) return full;
  if (handle) return handle;
  if (id) {
    // Uppercase-hex last-4 of a UUID. Stable per-coach, no PII, distinct
    // enough for 65k possibilities to avoid collisions at BAM's scale.
    const hex = String(id).replace(/-/g, "").slice(-4).toUpperCase();
    return `Coach ${hex}`;
  }
  return "Coach";
}

// Hydrate a list of profile IDs into display data (name, handle, avatar,
// badges). Goes through fetch_profile_display_by_ids (SECURITY DEFINER) —
// direct `.from('profiles').select(...)` is blocked by self-only RLS, and
// fetch_directory_members filters by stripe_subscription_active so historical
// comments from lapsed-sub or email-bypassed founder accounts came back
// empty (rendered as "Anonymous Coach"). The dedicated by-IDs RPC returns
// only public-safe display fields (no subscription filter).
async function hydrateProfileMap(ids) {
  const map = {};
  if (!ids || ids.length === 0) return map;
  const { data: profiles } = await supabase.rpc("fetch_profile_display_by_ids", { p_ids: ids });
  (profiles || []).forEach((p) => { map[p.id] = p; });
  return map;
}

// Attach a `lastReply` preview to each post that has at least one reply.
// One batch query for the replies + one for the replier profiles, regardless
// of how many posts are in the feed — bounded by the feed limit (default 50).
// Posts with zero replies are filtered out before the query runs, so this
// is a no-op on a fresh feed with no engagement.
async function attachLastReplies(posts) {
  const ids = (posts || []).filter(p => (p.replies?.length || 0) > 0).map(p => p.id);
  if (ids.length === 0) return posts;

  // Bounded: this is only a one-line "latest reply" preview, but the query
  // used to pull EVERY reply row for every post in the feed — unbounded,
  // so the payload (and load time) grew with total community engagement.
  // The newest 500 replies across a 50-post feed always contain the latest
  // reply for any post anyone is actually looking at; a post whose newest
  // reply is older than all 500 just skips the preview. (Fallback path
  // only — the 2026-08-23 fetch_community_feed RPC returns the preview
  // inline and skips this function entirely.)
  const { data: replyRows } = await supabase
    .from("community_replies")
    .select("id, post_id, user_id, content, created_at")
    .in("post_id", ids)
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(500);

  const replierIds = [...new Set((replyRows || []).map(r => r.user_id).filter(Boolean))];
  const replierMap = await hydrateProfileMap(replierIds);

  // Replies are ordered DESC by created_at, so the first row we see for any
  // given post_id is the latest. Stop after the first hit per post.
  const latestPerPost = {};
  (replyRows || []).forEach(r => {
    if (latestPerPost[r.post_id]) return;
    const profile = replierMap[r.user_id] || {};
    latestPerPost[r.post_id] = {
      author: displayName(profile),
      authorAvatar: avatarSrc(profile.avatar_url, 64) || null,
      content: r.content || "",
      createdAt: r.created_at,
    };
  });

  posts.forEach(p => { if (latestPerPost[p.id]) p.lastReply = latestPerPost[p.id]; });
  return posts;
}

/**
 * Fetch all community posts with author profile + reply count + like count.
 * Returns array of posts with shape compatible with existing PostCard component.
 */
export async function fetchCommunityPosts({ limit = 40, before = null, id = null } = {}) {
  // Primary path: fetch_community_feed RPC — one roundtrip, server-side
  // join of posts + authors + reply counts. Fast at any feed size.
  // Falls back to the legacy three-query flow if the RPC isn't deployed
  // yet (e.g. stale database during a migration rollout).
  //
  // `before` pages: posts older than that timestamp (pinned ones only come
  // on the first page). `id` fetches one post, for a deep link that lands
  // on something older than what's loaded. Both need the 2026-09-09 RPC;
  // against the older one the extra arguments make the call fail and the
  // legacy path below takes over.
  //
  // NOTE: production table uses author_id (not user_id) for the poster.
  const args = { match_limit: limit };
  if (before) args.before_created = before;
  if (id) args.p_id = id;
  const rpcRes = await supabase.rpc("fetch_community_feed", args);
  if (!rpcRes.error && Array.isArray(rpcRes.data)) {
    // The 2026-08-23 RPC returns the latest-reply preview + author badges
    // inline (lateral join server-side), collapsing what used to be three
    // serial roundtrips into one. Detect by column presence so this client
    // keeps working against the older deployed function until the
    // migration runs.
    const hasInlineLastReply = rpcRes.data.length > 0 && "last_reply_content" in rpcRes.data[0];
    const built = rpcRes.data.map(row => {
      const name = displayName({ id: row.author_id, first_name: row.first_name, last_name: row.last_name, handle: row.handle });
      const post = {
        id: row.id,
        userId: row.author_id,
        type: "post",
        title: row.title,
        content: row.content,
        tag: row.tag,
        media: [],
        embeds: Array.isArray(row.embeds) ? row.embeds : [],
        likes: row.likes || 0,
        replies: Array.from({ length: row.reply_count || 0 }),
        author: name,
        authorHandle: row.handle || "",
        authorRole: row.role || "",
        authorAvatar: avatarSrc(row.avatar_url, 64) || "",
        authorIsBamCoach: !!row.is_bam_coach,
        authorCertStatus: row.cert_status || "not_started",
        location: [row.city, row.country].filter(Boolean).join(", "),
        createdAt: row.created_at,
        pinnedAt: row.pinned_at || null,
        hiddenAt: row.hidden_at || null,
        time: formatRelativeTime(row.created_at),
      };
      if (hasInlineLastReply && row.last_reply_content != null) {
        post.lastReply = {
          author: displayName({
            id: row.last_reply_user_id,
            first_name: row.last_reply_first_name,
            last_name: row.last_reply_last_name,
            handle: row.last_reply_handle,
          }),
          authorAvatar: avatarSrc(row.last_reply_avatar_url, 64) || null,
          content: row.last_reply_content || "",
          createdAt: row.last_reply_created_at,
        };
      }
      return post;
    });
    return hasInlineLastReply ? built : await attachLastReplies(built);
  }

  // Fallback: legacy three-query flow. Kept as a safety net so a missing
  // RPC doesn't break Community.
  let legacy = supabase
    .from("community_posts")
    .select("id, author_id, title, content, tag, likes, created_at, embeds")
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (before) legacy = legacy.lt("created_at", before);
  if (id) legacy = legacy.eq("id", id);
  const { data: posts, error } = await legacy;

  if (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("fetchCommunityPosts:", error.message || error);
    }
    return [];
  }

  const userIds = [...new Set((posts || []).map(p => p.author_id).filter(Boolean))];
  const postIds = (posts || []).map(p => p.id);

  const [profilesRes, repliesRes] = await Promise.all([
    userIds.length > 0
      ? supabase.from("profiles").select("id, first_name, last_name, handle, role, avatar_url, city, country, is_bam_coach, cert_status").in("id", userIds)
      : Promise.resolve({ data: [] }),
    postIds.length > 0
      ? supabase.from("community_replies").select("post_id").in("post_id", postIds)
      : Promise.resolve({ data: [] }),
  ]);

  const profileMap = {};
  (profilesRes.data || []).forEach(p => { profileMap[p.id] = p; });
  const replyCountMap = {};
  (repliesRes.data || []).forEach(r => { replyCountMap[r.post_id] = (replyCountMap[r.post_id] || 0) + 1; });

  const built = (posts || []).map(p => {
    const author = profileMap[p.author_id] || {};
    const name = displayName(author);
    return {
      id: p.id,
      userId: p.author_id,
      type: p.type || "post",          // column not in production schema
      title: p.title,
      content: p.content,
      tag: p.tag,
      media: Array.isArray(p.media) ? p.media : [],  // column not in production schema
      embeds: Array.isArray(p.embeds) ? p.embeds : [],
      likes: p.likes || 0,
 replies: Array.from({ length: replyCountMap[p.id] || 0 }), // placeholder for count: full replies fetched on-demand
      author: name,
      authorHandle: author.handle || "",
      authorRole: author.role || "",
      authorAvatar: avatarSrc(author.avatar_url, 64) || null,
      authorLocation: [author.city, author.country].filter(Boolean).join(", "),
      // Badge state — passed through to PostCard for rendering CertBadge /
      // BamCoachBadge inline next to the author name. Defaults are safe
      // (no badge) for any author whose row predates the migration.
      authorIsBamCoach: !!author.is_bam_coach,
      authorCertStatus: author.cert_status || "not_started",
      ts: new Date(p.created_at).getTime(),
      createdAt: p.created_at,
      time: formatRelativeTime(p.created_at),
      // Legacy shape for PostCard MEMBERS lookup compatibility
      authorMeta: {
        initials: ((author.first_name?.[0] || "") + (author.last_name?.[0] || "")).toUpperCase() || "?",
        loc: [author.city, author.country].filter(Boolean).join(", ") || "",
        avatar_url: avatarSrc(author.avatar_url, 64),
      },
    };
  });
  return await attachLastReplies(built);
}

/**
 * Fetch replies for a specific post.
 */
export async function fetchReplies(postId) {
  // Primary path: one roundtrip. fetch_post_replies (2026-08-26) joins the
  // replies to their authors server-side. The legacy flow below cost two
  // or three serial requests — the reply query (retried without
  // parent_reply_id pre-migration) and then a separate profile hydrate —
  // which is what made comments feel slow to open.
  const rpcRes = await supabase.rpc("fetch_post_replies", { p_post_id: postId });
  if (!rpcRes.error && Array.isArray(rpcRes.data)) {
    return rpcRes.data.map((r) => ({
      id: r.id,
      userId: r.user_id,
      parentId: r.parent_reply_id || null,
      content: r.content,
      embeds: Array.isArray(r.embeds) ? r.embeds : [],
      author: displayName({ id: r.user_id, first_name: r.first_name, last_name: r.last_name, handle: r.handle }),
      authorHandle: r.handle || "",
      authorAvatar: avatarSrc(r.avatar_url, 64) || null,
      authorIsBamCoach: !!r.is_bam_coach,
      authorCertStatus: r.cert_status || "not_started",
      createdAt: r.created_at,
    }));
  }

  // Fallback (pre-migration): parent_reply_id ships in the 2026-08-09
  // threaded-replies migration; retry without it so threads still load flat.
  let { data, error } = await supabase
    .from("community_replies")
    .select("id, user_id, content, created_at, parent_reply_id")
    .eq("post_id", postId)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });
  if (error) {
    ({ data, error } = await supabase
      .from("community_replies")
      .select("id, user_id, content, created_at")
      .eq("post_id", postId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true }));
  }
  if (error) {
    if (process.env.NODE_ENV !== "production") console.warn("fetchReplies:", error.message || error);
    return [];
  }
  const userIds = [...new Set((data || []).map(r => r.user_id).filter(Boolean))];
  const profileMap = await hydrateProfileMap(userIds);
  return (data || []).map(r => {
    const author = profileMap[r.user_id] || {};
    return {
      id: r.id,
      // userId is needed by the client to render owner-only edit/delete
      // affordances. RLS still enforces the actual update/delete grants.
      userId: r.user_id,
      parentId: r.parent_reply_id || null,
      content: r.content,
      embeds: Array.isArray(r.embeds) ? r.embeds : [],
      author: displayName(author),
      authorHandle: author.handle || "",
      authorAvatar: avatarSrc(author.avatar_url, 64) || null,
      authorIsBamCoach: !!author.is_bam_coach,
      authorCertStatus: author.cert_status || "not_started",
      createdAt: r.created_at,
    };
  });
}

/**
 * Create a new post.
 */
// Upload a community attachment (image or PDF) to the community-uploads
// bucket. Path is scoped under the user's UUID so storage RLS gates
// writes to the actual uploader. Returns the public URL + mime/name
// so the caller can build an image / document embed.
export async function uploadCommunityAttachment(file) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const ext = (file.name?.split(".").pop() || "bin").toLowerCase().slice(0, 8);
  const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
  const { error } = await supabase.storage
    .from("community-uploads")
    .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from("community-uploads").getPublicUrl(path);
  return { url: data.publicUrl, name: file.name, type: file.type, size: file.size };
}

// Best-effort fetch of a YouTube video's title + thumbnail via the
// public oEmbed endpoint (no API key required, CORS-friendly). Used
// by the composer to upgrade pasted URLs into rich embeds without
// the coach having to type a title. Returns null on any failure so
// the caller can fall back to a generic title.
export async function fetchYouTubeOEmbed(url) {
  if (!url) return null;
  try {
    const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
    if (!res.ok) return null;
    const data = await res.json();
    return {
      title: data.title || null,
      thumbnail_url: data.thumbnail_url || null,
      author_name: data.author_name || null,
    };
  } catch {
    return null;
  }
}

export async function createPost({ title, content, tag, embeds, mentionIds }) {
  // Production community_posts schema is:
  //   id, author_id, title, content, tag, likes, embeds, is_pinned,
  //   is_flagged, created_at, deleted_at
  // The `embeds` JSONB column was added 2026-05-12; the client used to
  // also pass `type` and `media` but those were silently dropped — for
  // rich attachments use the embeds array (each entry self-contained,
  // see migration for the shape).
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const cleanEmbeds = Array.isArray(embeds) ? embeds : [];

  const { data, error } = await supabase
    .from("community_posts")
    .insert({
      author_id: user.id,
      title,
      content,
      tag,
      embeds: cleanEmbeds,
    })
    .select()
    .maybeSingle();
  if (error) throw error;
  // Fire analytics — feeds the Intelligence "Community" adoption + decision-fatigue
  // signals. Lazy-imported so this file stays usable in non-browser test contexts.
  try {
    const { logEvent } = await import("./analytics");
    logEvent("post_created", {
      post_id: data?.id,
      tag: tag || null,
      has_embeds: cleanEmbeds.length > 0,
      length: (content || "").length,
    });
  } catch {}
  // Fire-and-forget: push opted-in coaches about the new post (community_all,
  // default OFF). Server verifies it's the caller's fresh post before fanning out.
  if (data?.id) supabase.functions.invoke("notify-community-post", { body: { postId: data.id } }).catch(() => {});
  // Coaches tagged with @ get told. Server-side so it can check the post is
  // the caller's and fresh, and send each coach one push per post.
  if (data?.id && Array.isArray(mentionIds) && mentionIds.length) {
    supabase.auth.getSession().then(({ data: { session } }) =>
      fetch("/api/admin-actions?action=notify-mentions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` },
        body: JSON.stringify({ postId: data.id, userIds: mentionIds }),
      })
    ).catch(() => {});
  }
  return data;
}

/**
 * Add a reply to a post.
 */
export async function replyToPost(postId, content, { parentReplyId = null, targetUserId = null, embeds = null } = {}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  // Threaded reply: parent_reply_id points at the top-level reply this
  // comment belongs to (one level deep). Retry flat if the column hasn't
  // been migrated yet — the reply itself must never fail on threading, and
  // the same goes for an attached drill.
  const row = { post_id: postId, user_id: user.id, content };
  if (parentReplyId) row.parent_reply_id = parentReplyId;
  if (Array.isArray(embeds) && embeds.length) row.embeds = embeds;
  let { data, error } = await supabase
    .from("community_replies")
    .insert(row)
    .select()
    .single();
  if (error && /parent_reply_id|embeds|schema cache|42703/i.test(error.message || "")) {
    ({ data, error } = await supabase
      .from("community_replies")
      .insert({ post_id: postId, user_id: user.id, content })
      .select()
      .single());
  }
  if (error) throw error;

  try {
    const { logEvent } = await import("./analytics");
    logEvent("reply_created", { post_id: postId, reply_id: data?.id, length: (content || "").length });
  } catch {}

  // Fire-and-forget: notify the post author, and — for threaded replies —
  // the author of the comment being replied to. All self-notifications and
  // author/target duplicates are skipped (server re-checks too).
  (async () => {
    try {
      const { data: post } = await supabase
        .from("community_posts")
        .select("author_id, title")
        .eq("id", postId).maybeSingle();
      if (!post) return;
      const { data: replierProfile } = await supabase
        .from("profiles")
        .select("first_name, last_name, handle")
        .eq("id", user.id).maybeSingle();
      // In notification copy prefer the human-readable "A coach" over the
      // id-derived "Coach XXXX" fallback — the push feels warmer that way.
      const hasRealName = !!(replierProfile?.first_name || replierProfile?.last_name || replierProfile?.handle);
      const replierName = hasRealName ? displayName(replierProfile) : "A coach";
      const postUrl = `${window.location.origin}/#/platform/community`;
      if (post.author_id !== user.id) {
        await supabase.functions.invoke("send-email", {
          body: {
            kind: "community_reply",
            post_author_id: post.author_id,
            replier_name: replierName,
            post_title: post.title || "your post",
            reply_body: content,
            post_url: postUrl,
          },
        });
      }
      // Comment author (threaded reply target) — distinct copy, same pref.
      if (targetUserId && targetUserId !== user.id && targetUserId !== post.author_id) {
        supabase.functions.invoke("send-email", {
          body: {
            kind: "community_comment_reply",
            target_user_id: targetUserId,
            replier_name: replierName,
            post_title: post.title || "a post",
            reply_body: content,
            post_url: postUrl,
          },
        }).catch(() => {});
      }
      // Push both (gated on notification_prefs.community_replies). Old
      // deployed function ignores targetUserId — degrades to author-only.
      supabase.functions.invoke("notify-community-reply", { body: { postId, targetUserId } }).catch(() => {});
    } catch (e) {
      console.warn("community_reply email skipped:", e);
    }
  })();

  return data;
}

/**
 * Like/unlike a post. Returns new liked state.
 */
export async function toggleLike(postId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  // Check current state. maybeSingle: zero rows is a normal state here,
  // not an error (single() manufactured a PGRST116 on every first like).
  const { data: existing, error: checkErr } = await supabase
    .from("community_likes")
    .select("post_id")
    .eq("post_id", postId).eq("user_id", user.id).maybeSingle();
  if (checkErr) throw checkErr;

  if (existing) {
    const { error } = await supabase
      .from("community_likes").delete()
      .eq("post_id", postId).eq("user_id", user.id);
    // Errors must surface — swallowing them left the heart lying: the UI
    // flipped, nothing persisted, and the like "un-stuck" on next load.
    if (error) throw error;
    supabase.rpc("decrement_post_likes", { p_id: postId }).then(() => {}, () => {});
    return false;
  }

  const { error: insErr } = await supabase
    .from("community_likes")
    .insert({ post_id: postId, user_id: user.id });
  if (insErr) {
    // Unique-violation = the like already exists (double-tap or a second
    // device raced the check). That IS the liked state — report it as
    // success and don't bump the counter again.
    if (insErr.code === "23505" || /duplicate key|unique/i.test(insErr.message || "")) return true;
    throw insErr;
  }
  supabase.rpc("increment_post_likes", { p_id: postId }).then(() => {}, () => {});
  try {
    const { logEvent } = await import("./analytics");
    logEvent("like_given", { post_id: postId });
  } catch {}
  return true;
}

/**
 * Get set of post IDs the current user has liked.
 *
 * getSession (local storage read) instead of getUser (a network roundtrip
 * to the auth server just to learn our own id) — this runs on the feed's
 * critical path at boot, where every serial request shows up as loader time.
 */
export async function fetchMyLikes() {
  const { data: { session } } = await supabase.auth.getSession();
  const uid = session?.user?.id;
  if (!uid) return new Set();
  const { data } = await supabase.from("community_likes").select("post_id").eq("user_id", uid);
  return new Set((data || []).map(r => r.post_id));
}

/**
 * Bookmark/unbookmark a post. Returns new bookmarked state.
 * Backed by community_bookmarks (see
 * supabase/migrations/2026-07-03_community_bookmarks.sql).
 */
export async function toggleBookmark(postId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { data: existing, error: checkErr } = await supabase
    .from("community_bookmarks")
    .select("post_id")
    .eq("post_id", postId).eq("user_id", user.id).maybeSingle();
  if (checkErr) throw checkErr;

  if (existing) {
    const { error } = await supabase
      .from("community_bookmarks").delete()
      .eq("post_id", postId).eq("user_id", user.id);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase.from("community_bookmarks").insert({ post_id: postId, user_id: user.id });
  if (error) {
    if (error.code === "23505" || /duplicate key|unique/i.test(error.message || "")) return true;
    throw error;
  }
  return true;
}

/**
 * Get set of post IDs the current user has bookmarked.
 */
export async function fetchMyBookmarks() {
  // getSession, not getUser — same boot-path reasoning as fetchMyLikes.
  const { data: { session } } = await supabase.auth.getSession();
  const uid = session?.user?.id;
  if (!uid) return new Set();
  const { data } = await supabase.from("community_bookmarks").select("post_id").eq("user_id", uid);
  return new Set((data || []).map(r => r.post_id));
}

/**
 * Delete own post. Soft delete (deleted_at) — a hard delete took every
 * reply under the post with it, which read to repliers as "my comments
 * got deleted" with no trace. The feed and RPCs already filter
 * deleted_at, and moderation history stays auditable. Falls back to the
 * legacy hard delete if RLS doesn't allow the author to update the row.
 */
export async function deletePost(postId) {
  const { data, error } = await supabase
    .from("community_posts")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", postId)
    .select("id");
  if (!error && Array.isArray(data) && data.length > 0) return;
  const { error: hardErr } = await supabase.from("community_posts").delete().eq("id", postId);
  if (hardErr) throw hardErr;
}

/**
 * Report a post or reply as objectionable. Required by Apple Guideline 1.2 for
 * user-generated content. Records into community_reports for admin triage.
 * `contentType` is "post" | "reply". Returns silently if the user already
 * reported this content (the unique constraint just no-ops).
 */
export async function reportContent({ contentType, contentId, postId = null, reason = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in to report");
  const { error } = await supabase.from("community_reports").insert({
    reporter_id: user.id,
    content_type: contentType,
    content_id: contentId,
    post_id: postId,
    reason,
  });
  // Duplicate report (same user, same content) → treat as success.
  if (error && !/duplicate key|unique/i.test(error.message || "")) throw error;
}

/**
 * Soft-delete a reply you authored. We use deleted_at rather than a hard
 * delete so admins can audit moderation history and so reply counts on the
 * post stay consistent with the underlying row count if anything else
 * referenced it. fetchReplies filters out deleted_at-set rows so it
 * disappears from the thread immediately.
 *
 * RLS allows owners to UPDATE their own community_replies rows; service
 * role + admin policies allow broader delete (admin moderation path).
 */
export async function deleteReply(replyId) {
  const { error } = await supabase
    .from("community_replies")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", replyId);
  if (error) throw error;
}

/**
 * Edit a reply you authored. Only the `content` column is touched —
 * deleted_at, post_id, user_id, created_at all stay as-is. RLS scopes
 * the update to rows where user_id = auth.uid().
 */
export async function updateReply(replyId, content) {
  const trimmed = (content || "").trim();
  if (!trimmed) throw new Error("Reply cannot be empty");
  const { error } = await supabase
    .from("community_replies")
    .update({ content: trimmed })
    .eq("id", replyId);
  if (error) throw error;
}

/**
 * Fetch platform members for directory / leaderboard.
 *
 * Uses fetch_directory_members RPC because profiles RLS is self-only —
 * a direct .from('profiles').select(...) returns just the viewer's
 * own row, making the directory and leaderboard look empty. See
 * supabase/migrations/2026-05-04_member_directory_rpcs.sql.
 */
export async function fetchMembers({ limit = 200 } = {}) {
  const { data, error } = await supabase.rpc("fetch_directory_members", { p_limit: limit });
  if (error) { console.error("fetchMembers:", error); return []; }
  return (data || []).map(p => {
    const name = displayName(p);
    return {
      id: p.id,
      name,
      // Original first/last preserved so UserNameWithBadges can render
      // a profile-shaped object without re-splitting the joined name.
      first_name: p.first_name,
      last_name: p.last_name,
      initials: (p.first_name?.[0] || "") + (p.last_name?.[0] || ""),
      handle: p.handle,
      role: p.role,
      avatarUrl: avatarSrc(p.avatar_url, 64),
      city: p.city,
      country: p.country,
      loc: [p.city, p.country].filter(Boolean).join(", "),
      lat: p.latitude,
      lng: p.longitude,
      bio: p.bio,
      ig: p.ig,
      tw: p.x,
      is_bam_coach: !!p.is_bam_coach,
      cert_status: p.cert_status || "not_started",
    };
  });
}

/**
 * Aggregated leaderboard — counts posts per user, ordered by count.
 */
export async function fetchLeaderboard({ limit = 20 } = {}) {
  // Primary: fetch_community_leaderboard RPC (2026-08-23) — GROUP BY in
  // Postgres, one roundtrip. The legacy path below pulled up to 2000 post
  // rows to count in JS, then did a direct profiles select that self-only
  // RLS reduced to the viewer's own row — every other leader silently
  // dropped out of the list.
  const rpcRes = await supabase.rpc("fetch_community_leaderboard", { p_days: 30, p_limit: limit });
  if (!rpcRes.error && Array.isArray(rpcRes.data)) {
    return rpcRes.data.map(p => ({
      id: p.id,
      name: displayName(p),
      first_name: p.first_name,
      last_name: p.last_name,
      handle: p.handle,
      role: p.role,
      avatarUrl: avatarSrc(p.avatar_url, 64),
      is_bam_coach: !!p.is_bam_coach,
      cert_status: p.cert_status || "not_started",
      posts: p.posts || 0,
    }));
  }

  // Fallback (pre-migration): aggregate in JS, hydrate names through the
  // SECURITY DEFINER by-ids RPC (a direct profiles select is RLS-blocked).
  const { data: posts } = await supabase
    .from("community_posts")
    .select("author_id")
    .gte("created_at", new Date(Date.now() - 30 * 86400000).toISOString())
    .limit(2000);

  const counts = {};
  (posts || []).forEach(p => { counts[p.author_id] = (counts[p.author_id] || 0) + 1; });

  const userIds = Object.keys(counts).sort((a, b) => counts[b] - counts[a]).slice(0, limit);
  if (userIds.length === 0) return [];

  const profileMap = await hydrateProfileMap(userIds);

  return userIds.map(uid => {
    const p = profileMap[uid];
    if (!p) return null;
    return {
      id: uid,
      name: displayName(p),
      first_name: p.first_name,
      last_name: p.last_name,
      handle: p.handle,
      role: p.role,
      avatarUrl: avatarSrc(p.avatar_url, 64),
      is_bam_coach: !!p.is_bam_coach,
      cert_status: p.cert_status || "not_started",
      posts: counts[uid],
    };
  }).filter(Boolean);
}

// ── Admin moderation ────────────────────────────────────────────────────
// All of these hit SECURITY DEFINER RPCs in Supabase that check the caller's
// profiles.is_admin flag before acting. Non-admin callers get a 42501.

export async function adminDeletePost(postId) {
  const { error } = await supabase.rpc("admin_delete_post", { p_post_id: postId });
  if (error) throw error;
}

export async function adminDeleteReply(replyId) {
  const { error } = await supabase.rpc("admin_delete_reply", { p_reply_id: replyId });
  if (error) throw error;
}

export async function adminPinPost(postId, pinned) {
  const { error } = await supabase.rpc("admin_pin_post", { p_post_id: postId, p_pinned: pinned });
  if (error) throw error;
}

export async function adminHidePost(postId, hidden) {
  const { error } = await supabase.rpc("admin_hide_post", { p_post_id: postId, p_hidden: hidden });
  if (error) throw error;
}

export async function setAdminNote(userId, note) {
  const { error } = await supabase.rpc("set_admin_note", { p_user_id: userId, p_note: note });
  if (error) throw error;
}

export async function adminSetSuspended(userId, suspended) {
  const { error } = await supabase.rpc("admin_set_suspended", { p_user_id: userId, p_suspended: !!suspended });
  if (error) throw error;
}

// Toggle the BAM Coach staff flag on a user. Admin-only — wraps the
// admin_set_bam_coach RPC defined in 2026-05-03_phase0_profile_badges.sql.
// The RPC re-checks admin status and writes an audit_log row.
export async function adminSetBamCoach(userId, isBamCoach) {
  const { error } = await supabase.rpc("admin_set_bam_coach", {
    p_user_id: userId,
    p_is_bam_coach: !!isBamCoach,
  });
  if (error) throw error;
}

// Grant/revoke certification access for a user. Admin-only — wraps the
// admin_set_cert_purchased RPC (2026-06-05_admin_cert_toggle.sql), which
// re-checks admin status and writes an audit_log row. Setting it true unlocks
// the Certification section for that coach (no Stripe purchase required).
export async function adminSetCertPurchased(userId, value) {
  const { error } = await supabase.rpc("admin_set_cert_purchased", {
    p_user_id: userId,
    p_value: !!value,
  });
  if (error) throw error;
}

// Founding cert = bought the cert BEFORE the platform launched → full
// platform access for life, no subscription. Wraps admin_set_cert_founding
// (2026-07-30_cert_founding.sql): admin-gated + audited.
export async function adminSetCertFounding(userId, value) {
  const { error } = await supabase.rpc("admin_set_cert_founding", {
    p_user_id: userId,
    p_value: !!value,
  });
  if (error) throw error;
}

/* Test / internal account: left out of every admin count (C3, 2026-10-15). */
export async function adminSetMetricExclusion(userId, exclude, reason = null) {
  const { error } = await supabase.rpc("admin_set_metric_exclusion", {
    p_user: userId, p_exclude: !!exclude, p_reason: reason,
  });
  if (error) throw error;
}

export async function adminQuickPublishDrill(drillId) {
  const { error } = await supabase.rpc("admin_quick_publish_drill", { p_drill_id: drillId });
  if (error) throw error;
}

/**
 * Admin DM — sends an email to a specific user via the existing
 * send-email Edge Function. Simpler than building an in-app inbox; uses
 * the channel admins already have wired up.
 */
export async function adminSendDm({ userId, userEmail, subject, body }) {
  if (!userEmail && !userId) throw new Error("user email or id required");
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const res = await fetch("/api/admin-actions?action=send-dm", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ userId, userEmail, subject, body }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || res.statusText);
  }
  return res.json();
}

export async function adminFindDuplicateDrills() {
  const { data, error } = await supabase.rpc("admin_find_duplicate_drills");
  if (error) throw error;
  return data || [];
}

export async function fetchBrokenDrills() {
  const { data, error } = await supabase
    .from("drill_health")
    .select("drill_id, last_status, last_error, last_fail_at, checked_at")
    .not("last_fail_at", "is", null)
    .order("last_fail_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Admin: bulk-update drill rows. `patch` is a subset of
 * { category: string, type: "Game" | "On-Air" }. Returns rows affected.
 */
export async function adminBulkUpdateDrills(ids, patch) {
  if (!Array.isArray(ids) || ids.length === 0) return 0;
  const { data, error } = await supabase.rpc("admin_bulk_update_drills", {
    p_ids: ids,
    p_patch: patch,
  });
  if (error) throw error;
  return data;
}

/**
 * Admin: soft-delete many drills at once (sets deleted_at = now()).
 * Returns the number of rows affected.
 */
export async function adminBulkSoftDeleteDrills(ids) {
  if (!Array.isArray(ids) || ids.length === 0) return 0;
  const { data, error } = await supabase.rpc("admin_bulk_soft_delete_drills", { p_ids: ids });
  if (error) throw error;
  return data;
}

/**
 * Admin: set status on many drills at once
 * (draft | scheduled | published | archived).
 */
export async function adminBulkSetStatus(ids, status) {
  if (!Array.isArray(ids) || ids.length === 0) return 0;
  const { data, error } = await supabase.rpc("admin_bulk_set_status", {
    p_ids: ids,
    p_status: status,
  });
  if (error) throw error;
  return data;
}

/**
 * Admin: set coach_id on many drills at once.
 */
export async function adminBulkSetCoach(ids, coachId) {
  if (!Array.isArray(ids) || ids.length === 0) return 0;
  const { data, error } = await supabase.rpc("admin_bulk_set_coach", {
    p_ids: ids,
    p_coach_id: coachId,
  });
  if (error) throw error;
  return data;
}

/** Admin: list pending community video submissions. */
export async function fetchPendingSubmissions() {
  // Through the server, not the table. The table's row rules let a coach
  // see their own submissions and hid everyone else's from the admin too.
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch("/api/admin-actions?action=pending-submissions", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` },
    body: "{}",
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
  return Array.isArray(j.rows) ? j.rows : [];
}

export async function adminApproveSubmission(submissionId, { section = "pd", note = null } = {}) {
  const { data, error } = await supabase.rpc("admin_approve_submission", {
    p_submission_id: submissionId,
    p_section: section,
    p_note: note,
  });
  if (error) throw error;
  return data; // new drill id
}

export async function adminRejectSubmission(submissionId, note = null) {
  const { error } = await supabase.rpc("admin_reject_submission", {
    p_submission_id: submissionId,
    p_note: note,
  });
  if (error) throw error;
}

/** Admin: list pending community Pre-Made Session submissions. */
export async function fetchPendingSessions() {
  const { data, error } = await supabase
    .from("practice_sessions")
    .select("id, title, summary, focus_tags, duration_minutes, blocks, author_id, source, status, created_at")
    .eq("status", "pending")
    .order("created_at", { ascending: false });
  if (error) throw error;
  const rows = data || [];
  // Hydrate author display names from profiles. Best-effort — displayName
  // falls back to "Coach XXXX" (id-derived) when a profile row is missing
  // name fields, so this stops rendering "Anonymous Coach" across the
  // whole list when many coaches skipped profile setup.
  const ids = [...new Set(rows.map(r => r.author_id).filter(Boolean))];
  if (ids.length === 0) return rows.map(r => ({ ...r, author_name: displayName({ id: r.author_id }) }));
  const { data: profiles, error: profilesErr } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, handle")
    .in("id", ids);
  if (profilesErr) console.warn("fetchPendingSessions hydrate:", profilesErr.message);
  const nameMap = new Map((profiles || []).map(p => [p.id, displayName({ id: p.id, ...p })]));
  return rows.map(r => ({ ...r, author_name: nameMap.get(r.author_id) || displayName({ id: r.author_id }) }));
}

export async function adminReviewPracticeSession(sessionId, decision, note = null) {
  const { error } = await supabase.rpc("admin_review_practice_session", {
    p_id: sessionId,
    p_decision: decision,
    p_note: note,
  });
  if (error) throw error;
}

/**
 * Admin: fetch a richer profile view for the jump-to-user panel.
 * Goes through admin_fetch_user_profile RPC which bypasses RLS via
 * SECURITY DEFINER and pulls signup_at from auth.users (profiles has
 * no created_at column). Returns null if the caller isn't an admin
 * or the user doesn't exist.
 */
export async function fetchAdminUserProfile(userId) {
  const { data, error } = await supabase.rpc("admin_fetch_user_profile", { p_user_id: userId });
  if (error) { console.error("fetchAdminUserProfile:", error); return null; }
  return data?.[0] || null;
}
